import { useContext, useEffect, useMemo } from 'react';
import { SwitchThemeContext, useViewport } from '@tryhackme/thm-ui-components';
import { BREAKPOINTS } from 'src/common/constants/breakpoints';
import { TaskType } from 'src/common/enums/task';
import { ThemeColorMode } from 'src/common/enums';
import { AccordionDetails as TaskDescription } from '../../tasks/components/accordion-details';
import { QuestionAndAnswerSection } from '../..';
import { useResetUserProgressMutation, useTerminateVmMutation } from '../../../room.slice';
import { RoomNpsForm } from '../../room-nps-form/room-nps-form';
import { StyledLoading, StyledLoadingWrapper, StyledRoomContentWrapper } from './room-content.styles';
import { RoomContentProps } from './room-content.types';
import { TaskContentButton } from './task-content-button';
import { useRoomTaskBreakdownContext } from './room-task-breakdown-context';
export const RoomContent = ({
  joinRoom,
  hasUserJoinedRoom,
  attackBoxPrivateIP,
  autoAnswerQuestions,
  machineIP,
  activeMachine,
  roomDetails,
  roomProgress,
  tasks,
  glossaryData,
  ...taskDescriptionProps
}: RoomContentProps) => {
  const {
    currentTask,
    progressByCurrentTask
  } = useRoomTaskBreakdownContext();
  const {
    roomCode,
    handleClick
  } = taskDescriptionProps;
  const {
    width
  } = useViewport();
  const [terminateVM] = useTerminateVmMutation();
  const {
    theme
  } = useContext(SwitchThemeContext);
  const shouldShowNpsForm = useMemo(() => !!(roomDetails?.joined && roomDetails?.shouldSeeRoomNpsForm && roomProgress >= 30 && progressByCurrentTask >= 30), [progressByCurrentTask, roomDetails?.joined, roomDetails?.shouldSeeRoomNpsForm, roomProgress]);
  const isDarkMode = theme === ThemeColorMode.DARK;
  const isTaskWithVM = currentTask ? [TaskType.VM, TaskType.THM].includes(currentTask.type) : false;
  const [, {
    isLoading: isResetRoomProgressLoading
  }] = useResetUserProgressMutation({
    fixedCacheKey: 'shared-reset-progress'
  });
  useEffect(() => {
    if (currentTask && !isTaskWithVM && activeMachine) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      terminateVM(activeMachine.id);
    }
  }, [activeMachine, isTaskWithVM, terminateVM, currentTask]);
  if (!currentTask || !roomDetails || isResetRoomProgressLoading) {
    return <StyledLoadingWrapper data-testid="loading-wrapper">
        <StyledLoading data-testid="loading" />
        <StyledLoading data-testid="loading" />
        <StyledLoading data-testid="loading" />
      </StyledLoadingWrapper>;
  }
  const {
    _id: taskId,
    type,
    taskNo,
    description,
    title,
    uploadId,
    questions = []
  } = currentTask;
  const isTaskComplete = currentTask.questions.every(question => question.progress?.correct);
  const shouldSeeVmStart = !!hasUserJoinedRoom && width > BREAKPOINTS.MOBILE && isTaskWithVM;
  return <StyledRoomContentWrapper $isDark={isDarkMode} key={taskNo} onClick={handleClick} data-sentry-element="StyledRoomContentWrapper" data-sentry-component="RoomContent" data-sentry-source-file="room-content.tsx">
      <TaskDescription {...taskDescriptionProps} taskType={type} description={description} title={title} taskNo={taskNo} shouldSeeVmStart={shouldSeeVmStart} taskId={taskId} glossaryData={glossaryData} data-sentry-element="TaskDescription" data-sentry-source-file="room-content.tsx" />
      <TaskContentButton roomCode={roomCode} taskId={taskId} taskNo={taskNo} uploadId={uploadId} roomId={roomDetails._id} taskType={type} currentTask={currentTask} activeMachine={activeMachine} shouldSeeVmStart={shouldSeeVmStart} tasks={tasks} data-sentry-element="TaskContentButton" data-sentry-source-file="room-content.tsx" />
      <QuestionAndAnswerSection questions={questions} roomCode={roomCode} roomType={roomDetails.type} taskId={taskId} taskNo={taskNo} joinRoom={joinRoom} hasUserJoinedRoom={hasUserJoinedRoom} isTaskComplete={isTaskComplete} machineIP={machineIP} attackBoxPrivateIP={attackBoxPrivateIP} autoAnswerQuestions={autoAnswerQuestions} data-sentry-element="QuestionAndAnswerSection" data-sentry-source-file="room-content.tsx" />
      {shouldShowNpsForm && <RoomNpsForm roomCode={roomCode} roomProgress={roomProgress} />}
    </StyledRoomContentWrapper>;
};